import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Calendar, 
  AlertCircle 
} from "lucide-react";
import { 
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Typography,
  Snackbar,
  Tooltip
} from "@mui/material";
import Alert from "@mui/material/Alert";
import PaymentDetailsModal from "./PaymentDetailsModal";

const AdvanceBookFDPSlots = ({ onClose, selectedDomain, instituteBookings: propInstituteBookings, subTabs, eventType = "fdp" }) => {
  const [instituteBookings, setInstituteBookings] = useState(propInstituteBookings || null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentBooking, setSelectedPaymentBooking] = useState(null);
  const [domainsList, setDomainsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [calendarAvailability, setCalendarAvailability] = useState(null);
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  const getGlobalActiveBooking = () => {
    if (!instituteBookings) return null;

    const ab = instituteBookings.advance_booking || {};
    for (const key of Object.keys(ab)) {
      if (key === "canceled" || key === "cancelled") continue;
      if (Array.isArray(ab[key]) && ab[key].length > 0) {
        return { source: "booking", status: key, ...ab[key][0] };
      }
    }

    const interest = instituteBookings.interest || {};
    for (const key of Object.keys(interest)) {
      if (key === "cancelled" || key === "canceled") continue;
      if (Array.isArray(interest[key]) && interest[key].length > 0) {
        return { source: "interest", status: key, ...interest[key][0] };
      }
    }

    return null;
  };

  const activeInterest = getGlobalActiveBooking();

  const formatDateString = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusInfo = (statusVal) => {
    const statusMap = {
      0: { label: "Pending", colorClass: "text-amber-700 bg-amber-50 border-amber-200" },
      "0": { label: "Pending", colorClass: "text-amber-700 bg-amber-50 border-amber-200" },
      pending: { label: "Pending", colorClass: "text-amber-700 bg-amber-50 border-amber-200" },
      1: { label: "Approved", colorClass: "text-emerald-700 bg-emerald-50 border-emerald-200" },
      "1": { label: "Approved", colorClass: "text-emerald-700 bg-emerald-50 border-emerald-200" },
      approved: { label: "Approved", colorClass: "text-emerald-700 bg-emerald-50 border-emerald-200" },
      accepted: { label: "Approved", colorClass: "text-emerald-700 bg-emerald-50 border-emerald-200" },
      3: { label: "Booked", colorClass: "text-blue-700 bg-blue-50 border-blue-200" },
      "3": { label: "Booked", colorClass: "text-blue-700 bg-blue-50 border-blue-200" },
      booked: { label: "Booked", colorClass: "text-blue-700 bg-blue-50 border-blue-200" },
      4: { label: "Paid", colorClass: "text-emerald-800 bg-emerald-100 border-emerald-300" },
      "4": { label: "Paid", colorClass: "text-emerald-800 bg-emerald-100 border-emerald-300" },
      paid: { label: "Paid", colorClass: "text-emerald-800 bg-emerald-100 border-emerald-300" },
    };
    return statusMap[statusVal] || { label: "Unknown", colorClass: "text-slate-600 bg-slate-50 border-slate-200" };
  };

  // Custom toast notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showToast = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Form State
  const [formData, setFormData] = useState({
    trainerId: "0",
    eventType: eventType,
    state: "",
    institute: "0",
    domain: selectedDomain ? (selectedDomain.domain_id || selectedDomain.id)?.toString() : "",
  });
  // Selected dates for booking
  const [selectedDates, setSelectedDates] = useState([]); // [{ date: 'YYYY-MM-DD', availability_id: number }]
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);



  // Month navigation (shows 3 months at once)
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthOffset, setMonthOffset] = useState(0);
  const maxOffset = 4;

  const api = axios.create({
    baseURL: BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  useEffect(() => {
    // Immediately set propInstituteBookings if it exists so we don't wait for domain API
    if (propInstituteBookings) {
      setInstituteBookings(propInstituteBookings);
    }
    
    const fetchDomainsAndBookings = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("accessToken");

        // Fetch domains based on eventType (fdp) only if opening the booking modal (onClose exists)
        if (!!onClose && !selectedDomain) {
          const domainsResponse = await api.get(`/event/domainLists/${formData.eventType}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          setDomainsList(domainsResponse.data.domains || []);
        }

        if (!propInstituteBookings && !onClose) {
          try {
            const bookingsResponse = await api.get(`/event/get-interests/${formData.eventType}`, {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            });
            const bookingData = bookingsResponse.data;
            
            if (Array.isArray(bookingData) && bookingData.length > 0) {
              const interestGroup = {};
              const statusMap = {
                0: "pending",
                1: "approved",
                2: "cancelled",
                3: "booked",
                4: "paid",
              };
              bookingData.forEach(item => {
                const statusKey = statusMap[item.status] || "pending";
                if (!interestGroup[statusKey]) interestGroup[statusKey] = [];
                interestGroup[statusKey].push(item);
              });
              
              setInstituteBookings({
                interest: interestGroup
              });
            } else if (bookingData && typeof bookingData === 'object' && !Array.isArray(bookingData)) {
              const statusMap = {
                0: "pending",
                1: "approved",
                2: "cancelled",
                3: "booked",
                4: "paid",
              };
              const statusKey = statusMap[bookingData.status] || "pending";
              setInstituteBookings({
                interest: {
                  [statusKey]: [bookingData]
                }
              });
            } else {
              setInstituteBookings(null);
            }
          } catch (e) {
            console.error("Failed to fetch active bookings:", e);
          }
        }
      } catch (error) {
        console.error("Failed to fetch domains or active bookings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDomainsAndBookings();
  }, [propInstituteBookings, formData.eventType]);

  // getGlobalActiveBooking removed, replaced by state

  useEffect(() => {
    if (selectedDomain) {
      setFormData((prev) => ({
        ...prev,
        domain: (selectedDomain.domain_id || selectedDomain.id)?.toString(),
      }));
    }
  }, [selectedDomain]);

  useEffect(() => {
    setSelectedDates([]);
    setMonthOffset(0);
  }, [formData.domain]);
  const fetchCalendarAvailability = async () => {
    if (!formData.domain) {
      setCalendarAvailability(null);
      return;
    }
    try {
      setLoadingCalendar(true);
      const token = localStorage.getItem("accessToken");
      const response = await api.post(
        "/event/institute/calendar-availability",
        {
          domain_id: parseInt(formData.domain),
          event_type: formData.eventType,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setCalendarAvailability(response.data || null);
    } catch (error) {
      console.error("Failed to fetch calendar availability:", error);
      setCalendarAvailability(null);
    } finally {
      setLoadingCalendar(false);
    }
  };

  useEffect(() => {
    fetchCalendarAvailability();
  }, [formData.domain, formData.eventType]);

  // Format date key as 'YYYY-MM-DD'
  const formatDateKey = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getApiStatusForDate = (date) => {
    if (!calendarAvailability || !calendarAvailability.dates) return null;
    const dateKey = formatDateKey(date);
    const found = calendarAvailability.dates.find(item => item.date === dateKey);
    if (found) {
      const statusVal = parseInt(found.status);
      if ([0, 1, 2, 3, 4].includes(statusVal)) {
        const itemDomainId = found.domain_id || found.domain?.domain_id;
        if (itemDomainId && itemDomainId.toString() === formData.domain.toString()) {
          return found;
        }
        return null;
      }
      if (statusVal === 5 || parseInt(found.available_trainers) === 0) {
        return found;
      }
    }
    return null;
  };

  const getInstituteStatusForDate = (date) => {
    if (!instituteBookings || !formData.domain) return null;
    const dateKey = formatDateKey(date);

    const findInRange = (list) => {
      if (!list) return null;
      return list.find((item) => {
        const itemDomainId = item.domain_id || item.domain?.domain_id;
        const matchesDomain = itemDomainId ? itemDomainId.toString() === formData.domain.toString() : true;
        return matchesDomain && dateKey >= item.start_date && dateKey <= item.end_date;
      });
    };

    if (date.getDay() === 0) return null; // Sundays are always OFF

    const ab = instituteBookings.advance_booking || {};
    const interest = instituteBookings.interest || {};

    // 1. Confirmed Bookings (booked)
    if (findInRange(interest.booked)) return { type: "interest", status: "booked", label: "Booked" };

    // 2. Accepted / Approved
    if (findInRange(ab.accepted)) return { type: "advance_booking", status: "accepted", label: "Accepted" };
    if (findInRange(interest.approved)) return { type: "interest", status: "approved", label: "Approved" };

    // 3. Pending
    if (findInRange(ab.pending)) return { type: "advance_booking", status: "pending", label: "Pending" };
    if (findInRange(interest.pending)) return { type: "interest", status: "pending", label: "Pending Interest" };

    // 4. Rejected
    if (findInRange(ab.rejected)) return { type: "advance_booking", status: "rejected", label: "Rejected" };

    // 5. Cancelled / Canceled
    if (findInRange(ab.canceled)) return { type: "advance_booking", status: "canceled", label: "Canceled" };
    if (findInRange(interest.cancelled)) return { type: "interest", status: "cancelled", label: "Cancelled" };

    return null;
  };

  const getResolvedStatusForDate = (date) => {
    const instStatusObj = getInstituteStatusForDate(date);
    if (instStatusObj) {
      return instStatusObj.status;
    }
    return null;
  };

  const currentSelectedDomainObj = useMemo(() => {
    if (!formData.domain || !domainsList) return null;
    return domainsList.find(d => (d.domain_id || d.id).toString() === formData.domain.toString());
  }, [formData.domain, domainsList]);

  const interestDetails = useMemo(() => {
    if (!calendarAvailability || !calendarAvailability.dates) return [];
    
    const validDates = calendarAvailability.dates
      .filter(item => {
        const statusVal = parseInt(item.status);
        return [0, 1, 2, 3, 4].includes(statusVal);
      })
      .map(item => ({
        ...item,
        parsedDate: new Date(item.date)
      }))
      .sort((a, b) => {
        const aDomId = (a.domain_id || a.domain?.domain_id || "").toString();
        const bDomId = (b.domain_id || b.domain?.domain_id || "").toString();
        if (aDomId !== bDomId) {
          return aDomId.localeCompare(bDomId);
        }
        return a.parsedDate - b.parsedDate;
      });

    if (validDates.length === 0) return [];

    const groups = [];
    let currentGroup = [validDates[0]];

    for (let i = 1; i < validDates.length; i++) {
      const prev = validDates[i - 1];
      const curr = validDates[i];
      
      const diffTime = Math.abs(curr.parsedDate - prev.parsedDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      const prevDomainId = prev.domain_id || prev.domain?.domain_id;
      const currDomainId = curr.domain_id || curr.domain?.domain_id;

      const isConsecutive = 
        curr.status === prev.status && 
        prevDomainId?.toString() === currDomainId?.toString() &&
        (diffDays === 1 || (diffDays === 2 && (prev.parsedDate.getDay() === 6 || prev.parsedDate.getDay() === 0)));

      if (isConsecutive) {
        currentGroup.push(curr);
      } else {
        groups.push(currentGroup);
        currentGroup = [curr];
      }
    }
    groups.push(currentGroup);

    const statusLabels = {
      0: { label: "Pending", colorClass: "text-amber-700 bg-amber-50 border-amber-200" },
      1: { label: "Accepted", colorClass: "text-emerald-700 bg-emerald-50 border-emerald-200" },
      2: { label: "Rejected", colorClass: "text-purple-700 bg-purple-50 border-purple-200" },
      3: { label: "Booked", colorClass: "text-red-700 bg-red-50 border-red-200" },
      4: { label: "Paid", colorClass: "text-emerald-800 bg-emerald-100 border-emerald-300" }
    };

    const formatDateString = (d) => {
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    };

    return groups.map((group, index) => {
      const start = group[0].parsedDate;
      const end = group[group.length - 1].parsedDate;
      const dateRangeStr = start.getTime() === end.getTime() 
        ? formatDateString(start) 
        : `${formatDateString(start)} - ${formatDateString(end)}`;
      
      const statusVal = parseInt(group[0].status);
      const statusObj = statusLabels[statusVal] || { label: "Unknown", colorClass: "text-slate-600 bg-slate-50 border-slate-200" };

      const itemDomainId = group[0].domain_id || group[0].domain?.domain_id;
      const matchedDomain = domainsList.find(d => (d.domain_id || d.id).toString() === itemDomainId?.toString());
      const domainName = matchedDomain 
        ? (matchedDomain.domain_name || matchedDomain.name) 
        : (group[0].domain_name || group[0].domain?.domain_name || group[0].domain?.name || calendarAvailability.domain_name || "N/A");

      return {
        id: index,
        domainName: domainName,
        dateRange: dateRangeStr,
        statusLabel: statusObj.label,
        statusColorClass: statusObj.colorClass
      };
    });
  }, [calendarAvailability, domainsList]);

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d < today;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isFutureDateLimit = (date) => {
    const today = new Date();
    const currentMonthIdx = today.getMonth();
    const maxAllowedMonthIdx = currentMonthIdx + 5; // next 6 months (current month + 5)
    const maxYear = today.getFullYear() + Math.floor(maxAllowedMonthIdx / 12);
    const maxMonth = maxAllowedMonthIdx % 12;

    const d = new Date(date);
    const limitDate = new Date(maxYear, maxMonth + 1, 0, 23, 59, 59, 999);
    return d > limitDate;
  };

  const isFdpRestrictedWeek = (date) => {
    if (formData.eventType !== "fdp") return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);

    if (d < today) return false;

    const todayDay = today.getDay();
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() - todayDay);
    currentWeekStart.setHours(0, 0, 0, 0);

    const nextWeekEnd = new Date(currentWeekStart);
    nextWeekEnd.setDate(currentWeekStart.getDate() + 13);
    nextWeekEnd.setHours(23, 59, 59, 999);

    return d <= nextWeekEnd;
  };

  const isFdpWeekDisabled = (date) => {
    if (formData.eventType !== "fdp") return false;

    const d = new Date(date);
    const day = d.getDay();
    const sunday = new Date(d);
    sunday.setDate(d.getDate() - day);
    sunday.setHours(0, 0, 0, 0);

    for (let i = 1; i <= 6; i++) {
      const current = new Date(sunday);
      current.setDate(sunday.getDate() + i);
      if (isPastDate(current) || isFdpRestrictedWeek(current) || isFutureDateLimit(current)) {
        return true;
      }
      
      if (getApiStatusForDate(current)) {
        return true;
      }

      const status = getResolvedStatusForDate(current);
      if (status && status !== "cancelled" && status !== "canceled") {
        return true;
      }
    }
    return false;
  };

  // Disabled day check (Condition 2 & 3 & 6-month limit)
  const isDateDisabled = (checkDate) => {
    if (activeInterest) return true;

    const d = new Date(checkDate);
    // 1. Disable past days (Condition 2)
    if (isPastDate(d)) return true;

    // 2. Sundays are not bookable / disabled
    if (d.getDay() === 0) return true;

    // 3. Condition 3: FDP is 6 day program, cannot select current week and next week
    if (isFdpRestrictedWeek(d)) return true;

    // 4. Disable dates beyond next 6 months
    if (isFutureDateLimit(d)) return true;

    // 5. Disable whole week if any block day is invalid for FDP
    if (isFdpWeekDisabled(d)) return true;

    // 5b. Check backend calendar availability response
    if (getApiStatusForDate(d)) {
      return true;
    }

    // 6. Disable if already booked or has active resolved status
    const status = getResolvedStatusForDate(d);
    if (status && status !== "cancelled" && status !== "canceled") {
      return true;
    }

    return false;
  };

  // Date selection logic (Condition 1)
  const handleDateClick = (clickedDate, isCellDisabled) => {
    const globalActive = activeInterest;
    if (globalActive) {
      let statusMsg = "";
      if (globalActive.status === "accepted" || globalActive.status === "approved" || globalActive.status === "paid") {
        statusMsg = "Accepted / Approved";
      } else if (globalActive.status === "pending" || globalActive.status === "inprogress" || globalActive.status === "payment_pending") {
        statusMsg = "Pending";
      } else if (globalActive.status === "rejected") {
        statusMsg = "Rejected";
      } else if (globalActive.status === "booked") {
        statusMsg = "Booked";
      }
      showToast(`You already have a booking / interest with status: ${statusMsg}.`, "warning");
      return;
    }

    if (!formData.trainerId || !formData.eventType) return;

    // Check if there is an active resolved status on or after today for this institute
    const instStatusObj = getInstituteStatusForDate(clickedDate);
    const isPast = isPastDate(clickedDate);

    if (instStatusObj && instStatusObj.status !== 'cancelled' && instStatusObj.status !== 'canceled' && !isPast) {
      const resolvedStatus = instStatusObj.status;
      let statusMsg = resolvedStatus.toUpperCase();
      if (resolvedStatus === "accepted" || resolvedStatus === "approved" || resolvedStatus === "paid") {
        statusMsg = "Accepted / Approved";
      } else if (resolvedStatus === "pending" || resolvedStatus === "inprogress" || resolvedStatus === "payment_pending") {
        statusMsg = "Pending";
      } else if (resolvedStatus === "rejected") {
        statusMsg = "Rejected";
      } else if (resolvedStatus === "booked") {
        statusMsg = "Booked";
      }
      showToast(`You already have a booking request for this date that is ${statusMsg}.`, "warning");
      return;
    }

    if (isCellDisabled) return;

    // FDP Selection Condition (Condition 1 & 2 & 3)
    if (formData.eventType === "fdp") {
      const d = new Date(clickedDate);
      const day = d.getDay(); // 0 (Sun) to 6 (Sat)
      const sunday = new Date(d);
      sunday.setDate(d.getDate() - day);
      sunday.setHours(0, 0, 0, 0);

      const monday = new Date(sunday);
      monday.setDate(sunday.getDate() + 1);
      const mondayKey = formatDateKey(monday);

      const isAlreadySelected = selectedDates.some((s) => s.date === mondayKey);

      if (isAlreadySelected) {
        setSelectedDates([]);
        return;
      }

      const selected = [];
      for (let i = 1; i <= 6; i++) {
        const current = new Date(sunday);
        current.setDate(sunday.getDate() + i);
        const key = formatDateKey(current);
        selected.push({
          date: key,
          availability_id: null,
        });
      }

      setSelectedDates(selected);
    } else {
      // EDP or Tech Camp (Single click selection)
      const dateKey = formatDateKey(clickedDate);
      const isAlreadySelected = selectedDates.some((d) => d.date === dateKey);

      if (isAlreadySelected) {
        setSelectedDates((prev) => prev.filter((d) => d.date !== dateKey));
      } else {
        setSelectedDates((prev) => [...prev, { date: dateKey, availability_id: null }]);
      }
    }
  };

  // Check if a date is currently selected
  const isDateSelected = (date) => {
    const key = formatDateKey(date);
    return selectedDates.some((d) => d.date === key);
  };

  const handleBookSelectedClick = () => {
    if (selectedDates.length === 0) {
      showToast("Please select at least one date from the calendar.", "warning");
      return;
    }
    setShowConfirmPopup(true);
  };

  // Submit Booking
  const handleSubmitBooking = async () => {
    if (selectedDates.length === 0) {
      showToast("Please select at least one date from the calendar.", "warning");
      return;
    }
    if (!formData.eventType || !formData.domain) {
      showToast("Please fill all required fields marked with *", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        dates: selectedDates.map((d) => d.date),
        domain_id: parseInt(formData.domain),
        is_paid: 1,
      };

      const response = await api.post("/event/institute/submit-interest2", payload);
      const isSuccess = response.status === 200 && 
                        response.data?.status !== false && 
                        response.data?.status !== "error" && 
                        response.data?.success !== false;

      if (isSuccess) {
        showToast(response.data?.message || "Interest submitted successfully!", "success");
        setSelectedDates([]);
        await fetchCalendarAvailability();
        // Refresh bookings using the new get-interests API
        if (!propInstituteBookings) {
          try {
            const token = localStorage.getItem("accessToken");
            const bookingsResponse = await api.get(`/event/get-interests/${formData.eventType}`, {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            });
            const bookingData = bookingsResponse.data;
            let hasActiveBooking = false;
            let activeBookingData = null;
            if (bookingData && typeof bookingData !== 'string') {
              hasActiveBooking = true;
              activeBookingData = bookingData;
            } else if (bookingData && typeof bookingData === 'string' && bookingData !== "No interests found.") {
              hasActiveBooking = true;
              activeBookingData = bookingData;
            }

            if (hasActiveBooking) {
              setInstituteBookings({
                interest: {
                  [activeBookingData.status || 0]: [activeBookingData]
                }
              });
            } else {
              setInstituteBookings(null);
            }
          } catch (e) {
            console.error("Failed to refresh bookings:", e);
          }
        }
        setTimeout(() => {
          if (onClose) onClose(true);
        }, 1500);
      } else {
        showToast(response.data?.message || "Failed to submit booking.", "error");
      }
    } catch (error) {
      console.error("Booking error:", error);
      showToast(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        "Failed to submit booking.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };



  // Helper: Get list of 5 months starting from the current month
  const visibleMonths = useMemo(() => {
    const list = [];
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-11
    for (let i = 0; i < 5; i++) {
      const d = new Date(currentYear, currentMonth + i, 1);
      list.push(d);
    }
    return list;
  }, []);

  // Generate 42 cells grid for calendar
  const generateDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDay = firstDay.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const prevMonthDays = [];
    for (let i = 0; i < startDay; i++) {
      const d = new Date(year, month, i - startDay + 1);
      prevMonthDays.push({ date: d, day: d.getDate(), isCurrentMonth: false });
    }

    const currentMonthDays = [];
    for (let i = 1; i <= daysInMonth; i++) {
      currentMonthDays.push({ date: new Date(year, month, i), day: i, isCurrentMonth: true });
    }

    const totalDisplayed = prevMonthDays.length + currentMonthDays.length;
    const nextMonthDays = [];
    for (let i = 1; totalDisplayed + nextMonthDays.length < 42; i++) {
      const d = new Date(year, month + 1, i);
      nextMonthDays.push({ date: d, day: i, isCurrentMonth: false });
    }

    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const daysOfWeek = ["SU", "M", "T", "W", "Th", "F", "SA"];


  return (
    <div className="flex flex-col bg-slate-50">
      
      {/* 1. Persistent Header with Domain Selector */}
      {!selectedDomain && !!onClose && (
        <div className={`sticky top-0 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between py-4 bg-white border-b border-slate-200 gap-4 shadow-sm ${onClose ? 'pl-14 pr-14' : 'px-6'}`}>
          <div className="flex items-center gap-3 justify-start w-full sm:w-auto">
            {/* Domain Dropdown */}
            {activeInterest === null && !!onClose && (
              <TextField
                select
                label="Select Domain"
                value={formData.domain}
                onChange={(e) => {
                  const selectedDomId = e.target.value.toString();
                  setFormData((prev) => ({
                    ...prev,
                    domain: selectedDomId,
                  }));
                  setSelectedDates([]); // Reset dates when domain changes to avoid issues
                }}
                sx={{
                  minWidth: { xs: "100%", sm: 350 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                    "& fieldset": {
                      borderColor: "#cbd5e1",
                      borderWidth: "1.5px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#94a3b8",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2563eb",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#64748b",
                    fontWeight: 500,
                    "&.Mui-focused": {
                      color: "#2563eb",
                    }
                  },
                  "& .MuiSelect-select": {
                    fontWeight: 600,
                    color: "#1e293b",
                  }
                }}
              >
                {domainsList.map((dom) => (
                  <MenuItem key={dom.domain_id || dom.id} value={(dom.domain_id || dom.id).toString()}>
                    {dom.domain_name || dom.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

          </div>
          {subTabs && (
            <div className="flex items-center ml-auto">
              {subTabs}
            </div>
          )}
        </div>
      )}

      {/* 2. Main Content Area */}
      <div className="px-6 pt-6 pb-2 gap-6 flex flex-col">
        {!onClose && subTabs && (
          <div className="flex justify-end w-full mb-2">
            {subTabs}
          </div>
        )}
        {loading && !onClose ? (
          <div className="flex-1 flex flex-col items-center justify-center bg-white border border-slate-200 rounded-2xl p-8 shadow-sm text-center my-auto min-h-[250px]">
            <CircularProgress size={40} thickness={4} />
            <h3 className="text-slate-700 font-semibold text-base mt-4">Loading Bookings...</h3>
          </div>
        ) : activeInterest !== null ? (
          <div className="flex flex-col gap-6">
    
            {/* Active Interest Table */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <h4 className="text-slate-800 font-bold text-sm mb-4 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-indigo-600"></span>
                Interest & Booking Details
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 text-slate-500 font-semibold text-xs">
                      <th className="pb-3 pr-4">Domain Name</th>
                      <th className="pb-3 px-4">Date Range</th>
                      <th className="pb-3 px-4 text-center">Status</th>
                      <th className="pb-3 pl-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700 text-xs font-semibold">
                    <tr className="hover:bg-slate-50/55 transition-colors">
                      <td className="py-3.5 pr-4 text-slate-800 font-bold">{activeInterest.domain_name}</td>
                      <td className="py-3.5 px-4 text-slate-600 font-medium">
                        {`${formatDateString(activeInterest.start_date)} - ${formatDateString(activeInterest.end_date)}`}
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold border ${getStatusInfo(activeInterest.status).colorClass}`}>
                          {getStatusInfo(activeInterest.status).label}
                        </span>
                      </td>
                      <td className="py-3.5 pl-4 text-right">
                        {(activeInterest.status === "approved" ||
                          activeInterest.status === "accepted" ||
                          activeInterest.status === 1 ||
                          activeInterest.status === "1" ||
                          activeInterest.status === "payment_pending") ? (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                              setSelectedPaymentBooking(activeInterest);
                              setPaymentModalOpen(true);
                            }}
                            sx={{
                              fontSize: "11px",
                              fontWeight: 700,
                              py: 0.5,
                              px: 1.8,
                              borderRadius: "8px",
                              backgroundColor: "#1976d2",
                              boxShadow: "none",
                              textTransform: "uppercase",
                              "&:hover": {
                                backgroundColor: "#1565c0",
                                boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
                              },
                            }}
                          >
                            Pay Now
                          </Button>
                        ) : (
                          <Typography fontSize={12} color="text.secondary">
                            -
                          </Typography>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : !onClose ? (
          <div className="flex-1 flex flex-col items-center justify-center bg-white border border-slate-200 rounded-2xl p-8 shadow-sm text-center my-auto">
            <h3 className="text-slate-800 font-bold text-lg mb-2">No Active Bookings</h3>
            <p className="text-slate-500 text-sm max-w-md font-medium">
              You do not have any active bookings. Click "Book Your Slot" to submit a new interest.
            </p>
          </div>
        ) : !formData.domain ? (
          <div className="flex-1 flex flex-col items-center justify-center bg-white border border-slate-200 rounded-2xl p-8 shadow-sm text-center my-auto">
            <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mb-4">
              <Calendar className="w-8 h-8" />
            </div>
            <h3 className="text-slate-800 font-bold text-lg mb-2">Select a Domain</h3>
            <p className="text-slate-500 text-sm max-w-md font-medium">
              Please select a domain to display the calendar.
            </p>
          </div>
        ) : (
          <>


            {/* Calendars and Legend (Side-by-Side View) */}
            <div className="w-full max-w-6xl mx-auto flex flex-col lg:flex-row justify-center items-start gap-6 relative">
              {loadingCalendar && (
                <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-50 flex items-center justify-center rounded-2xl">
                  <div className="flex flex-col items-center gap-2">
                    <CircularProgress size={40} />
                    <p className="text-slate-500 text-xs font-semibold">Updating availability...</p>
                  </div>
                </div>
              )}
              
              {/* Calendar Column */}
              <div className="flex flex-col gap-4 flex-1 lg:max-w-[440px]">
                {[0].map((i) => {
                  const currentMonthDate = visibleMonths[monthOffset + i];
                  if (!currentMonthDate) return null;

                  const monthDays = generateDays(currentMonthDate);

                  return (
                    <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                      {/* Month name with Navigation */}
                      <div className="flex items-center justify-between font-bold text-slate-800 mb-3 border-b pb-2 text-lg">
                        <button
                          disabled={monthOffset === 0}
                          onClick={() => setMonthOffset((prev) => prev - 1)}
                          className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center text-slate-600"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <span>{monthNames[currentMonthDate.getMonth()]} {currentMonthDate.getFullYear()}</span>
                        <button
                          disabled={monthOffset >= maxOffset}
                          onClick={() => setMonthOffset((prev) => prev + 1)}
                          className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center text-slate-600"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>
                      </div>

                      {/* Weekdays */}
                      <div className="grid grid-cols-7 text-center text-sm font-bold text-slate-500 mb-3">
                        {daysOfWeek.map((day) => (
                          <div key={day} className="py-1">{day}</div>
                        ))}
                      </div>

                      {/* Day cells */}
                      <div className="grid grid-cols-7 gap-1">
                        {monthDays.map((dayObj, index) => {
                          if (!dayObj.isCurrentMonth) {
                            return <div key={index} className="aspect-square border border-transparent"></div>;
                          }

                          const dateKey = formatDateKey(dayObj.date);
                          const selected = isDateSelected(dayObj.date);
                          const isSunday = dayObj.date.getDay() === 0;
                          const disabled = isDateDisabled(dayObj.date);

                          let cellClass = "aspect-square flex items-center justify-center rounded-lg border text-sm font-semibold select-none transition-all duration-150 ";
                          const today = isToday(dayObj.date);

                          const instStatusObj = getInstituteStatusForDate(dayObj.date);
                          const resolvedStatus = instStatusObj ? instStatusObj.status : null;

                          const apiStatusObj = getApiStatusForDate(dayObj.date);
                          const apiStatusStr = apiStatusObj ? apiStatusObj.status.toString() : null;

                          if (selected) {
                            cellClass += "bg-blue-600 text-white border-blue-600 shadow-md scale-105 cursor-pointer";
                          } else if (apiStatusStr === "1") {
                            cellClass += "bg-emerald-50 text-emerald-700 border-emerald-200 cursor-not-allowed";
                          } else if (apiStatusStr === "0") {
                            cellClass += "bg-amber-50 text-amber-700 border-amber-200 cursor-not-allowed";
                          } else if (apiStatusStr === "2") {
                            cellClass += "bg-purple-50 text-purple-700 border-purple-200 cursor-not-allowed";
                          } else if (apiStatusStr === "3") {
                            cellClass += "bg-red-50 text-red-700 border-red-200 cursor-not-allowed";
                          } else if (apiStatusStr === "4") {
                            cellClass += "bg-emerald-50 text-emerald-700 border-emerald-200 cursor-not-allowed";
                          } else if (apiStatusStr === "5") {
                            cellClass += "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed";
                          } else if (today) {
                            cellClass += "bg-blue-600 text-white border-blue-600 shadow-sm font-bold " + (disabled ? "cursor-not-allowed opacity-80" : "cursor-pointer");
                          } else if (resolvedStatus === "accepted" || resolvedStatus === "approved" || resolvedStatus === "paid") {
                            cellClass += "bg-emerald-50 text-emerald-700 border-emerald-200 cursor-not-allowed";
                          } else if (resolvedStatus === "pending" || resolvedStatus === "inprogress" || resolvedStatus === "payment_pending") {
                            cellClass += "bg-amber-50 text-amber-700 border-amber-200 cursor-not-allowed";
                          } else if (resolvedStatus === "rejected") {
                            cellClass += "bg-purple-50 text-purple-700 border-purple-200 cursor-not-allowed";
                          } else if (resolvedStatus === "booked") {
                            cellClass += "bg-red-50 text-red-700 border-red-200 cursor-not-allowed";
                          } else if (resolvedStatus === "cancelled" || resolvedStatus === "canceled") {
                            cellClass += "bg-slate-50 text-slate-500 border-slate-200 cursor-not-allowed";
                          } else if (disabled) {
                            cellClass += "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed";
                          } else if (formData.trainerId) {
                            const restricted = isFdpRestrictedWeek(dayObj.date);
                            cellClass += `bg-white text-slate-700 border-slate-200 ${restricted ? "cursor-not-allowed" : "hover:border-blue-400 cursor-pointer"}`;
                          } else {
                            const restricted = isFdpRestrictedWeek(dayObj.date);
                            cellClass += `bg-white text-slate-700 border-slate-200 ${restricted ? "cursor-not-allowed" : "hover:border-slate-300 cursor-pointer"}`;
                          }

                          const cellEl = (
                            <div
                              onClick={() => handleDateClick(dayObj.date, disabled)}
                              className={cellClass}
                            >
                              <span className={selected || today ? "text-white font-bold" : disabled ? "text-slate-400" : "text-slate-700"}>
                                {dayObj.day}
                              </span>
                            </div>
                          );

                          if (apiStatusStr === "5") {
                            return (
                              <Tooltip key={index} title="Trainer Unavailable" arrow placement="top">
                                {cellEl}
                              </Tooltip>
                            );
                          } else if (disabled && !isSunday) {
                            return (
                              <Tooltip key={index} title="Trainer Unavailable" arrow placement="top">
                                {cellEl}
                              </Tooltip>
                            );
                          }

                          return (
                            <React.Fragment key={index}>
                              {cellEl}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right Column: Legend and Action */}
              <div className="flex flex-col lg:w-[280px] shrink-0 gap-4">
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-col gap-5">
                  {/* Status Legend */}
                  <div className="flex flex-col gap-4">
                    <h3 className="font-bold text-slate-800 text-sm border-b pb-2">Status Legend:</h3>
                    <div className="flex flex-col gap-y-3">
                      <div className="flex items-center gap-3">
                        <span className="w-4 h-4 rounded-full bg-emerald-500 inline-block shadow-sm shrink-0"></span>
                        <span className="text-slate-700 text-sm font-medium">Accepted</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="w-4 h-4 rounded-full bg-amber-500 inline-block shadow-sm shrink-0"></span>
                        <span className="text-slate-700 text-sm font-medium">Pending</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="w-4 h-4 rounded-full bg-blue-600 inline-block shadow-sm shrink-0"></span>
                        <span className="text-slate-700 text-sm font-medium">Selected</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="w-4 h-4 rounded-full bg-red-500 inline-block shadow-sm shrink-0"></span>
                        <span className="text-slate-700 text-sm font-medium">Booked</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="w-4 h-4 rounded-full bg-purple-500 inline-block shadow-sm shrink-0"></span>
                        <span className="text-slate-700 text-sm font-medium">Rejected</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="w-4 h-4 rounded-full bg-slate-400 inline-block shadow-sm shrink-0"></span>
                        <span className="text-slate-700 text-sm font-medium">Cancelled</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="w-4 h-4 rounded-full bg-slate-100 border border-slate-300 inline-block shadow-sm shrink-0"></span>
                        <span className="text-slate-700 text-sm font-medium leading-tight">N/A<br/><span className="text-xs text-slate-500">(Trainer Unavailable)</span></span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Area */}
                  <div className="pt-5 border-t border-slate-100 flex flex-col gap-4 mt-auto">
                    <div className="flex items-center justify-between w-full">
                      {selectedDates.length > 0 ? (
                        <div className="flex items-center justify-center w-full gap-2 text-sm font-semibold text-blue-700 bg-blue-50 px-3 py-2 rounded-lg border border-blue-100">
                          <span>{selectedDates.length} days selected</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center w-full gap-2 text-sm font-semibold text-slate-500 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200">
                          <span>No dates selected</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-3 w-full">
                      <button
                        onClick={handleBookSelectedClick}
                        disabled={submitting || selectedDates.length === 0}
                        className={`w-full px-6 py-2.5 rounded-lg font-bold text-sm shadow-sm transition-all ${
                          selectedDates.length > 0
                            ? "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md"
                            : "bg-slate-100 text-slate-400 cursor-not-allowed"
                        }`}
                      >
                        {submitting ? "Submitting..." : "Submit Interest"}
                      </button>

                      {selectedDates.length > 0 && (
                        <button
                          onClick={() => setSelectedDates([])}
                          className="w-full px-4 py-2 bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors font-semibold text-sm shadow-sm"
                        >
                          Clear Selection
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>


          </>
        )}
      </div>





      {/* CONFIRMATION POPUP */}
      <Dialog
        open={showConfirmPopup}
        onClose={() => setShowConfirmPopup(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0px 8px 30px rgba(0,0,0,0.12)",
            p: 3,
            maxWidth: "360px",
            width: "100%",
          },
        }}
      >
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-4 text-blue-600">
            <Calendar className="w-6 h-6" />
          </div>
          <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: 700, color: "#1E293B", mb: 1 }}>
            Confirm
          </Typography>
          <Typography sx={{ fontSize: "13.5px", fontWeight: 500, color: "#64748B", mb: 3, px: 1 }}>
            Are you sure you want to submit this request?
          </Typography>
          <div className="flex gap-3 w-full justify-center">
            <Button
              variant="outlined"
              onClick={() => setShowConfirmPopup(false)}
              sx={{ 
                textTransform: "none", 
                fontWeight: 600, 
                borderRadius: "10px",
                flex: 1,
                borderColor: "#E2E8F0",
                color: "#64748B",
                "&:hover": {
                  backgroundColor: "#F8FAFC",
                  borderColor: "#CBD5E1",
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={submitting}
              onClick={async () => {
                setShowConfirmPopup(false);
                await handleSubmitBooking();
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                flex: 1,
                boxShadow: "none",
                background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)",
                  boxShadow: "none",
                },
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Dialog>

      {/* TOAST NOTIFICATIONS */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "8px", fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* PAYMENT DETAILS MODAL */}
      <PaymentDetailsModal
        open={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setSelectedPaymentBooking(null);
        }}
        booking={selectedPaymentBooking}
        eventType="fdp"
      />
    </div>
  );
};

export default AdvanceBookFDPSlots;
